import { useMemo } from 'react';

import type { CurrencyCode, ExchangeRate, SupportedCurrencyCode } from 'apis/handler/exchange-rate';
import { useCurrencyStore } from 'store/useCurrencyStore';
import { useExchangeRateStore } from 'store/useExchangeRateStore';

import { formatForeign } from './currencyFormat';

export * from './currencyFormat';

/**
 * 선택된 통화 + 환율 캐시를 조합해 외화 표시 문자열 반환.
 * - 'KRW' 선택(=표시 안 함) → null
 * - 환율 캐시 비어있음 → null
 * - 선택한 통화가 응답에 없음 → null (호출 측에서 KRW만 보여주면 됨)
 */
export const useForeignPrice = (krw: number | null | undefined): string | null => {
  const selectedCurrency = useCurrencyStore((s) => s.selectedCurrency);
  const rates = useExchangeRateStore((s) => s.rates);

  if (selectedCurrency === 'KRW') return null;
  if (krw == null || Number.isNaN(krw)) return null;
  if (!rates || rates.length === 0) return null;

  const rate = findRate(rates, selectedCurrency);
  if (rate == null) return null;

  return formatForeign(krw, rate, selectedCurrency);
};

const findRate = (rates: ExchangeRate[], code: SupportedCurrencyCode): number | null => {
  const found = rates.find((r) => r.currency === code);
  return found ? found.rate : null;
};

/**
 * 응답(또는 캐시)에 존재하는 통화 코드 set 반환. 모달에서 비활성화 표시용.
 * useMemo로 stable reference 유지 → 하위 컴포넌트의 불필요한 리렌더 방지.
 */
export const useAvailableCurrencyCodes = (): Set<CurrencyCode> => {
  const rates = useExchangeRateStore((s) => s.rates);
  return useMemo(() => {
    const set = new Set<CurrencyCode>(['KRW']);
    rates.forEach((r) => set.add(r.currency));
    return set;
  }, [rates]);
};
